import type { GlobusProfession } from './landingContentShared.js';

export type GlobusCaptionLanguage = 'de' | 'en' | 'tr';

export interface GlobusCaptionTrack {
  src: string;
  srclang: GlobusCaptionLanguage;
  label: string;
  default?: boolean;
}

const emptyGlobusCaptionTracks = (): GlobusCaptionTrack[] => [
  { src: '', srclang: 'de', label: 'Deutsch' },
  { src: '', srclang: 'en', label: 'Englisch' },
  { src: '', srclang: 'tr', label: 'Türkisch' },
];

export interface GlobusJobContent {
  profession: GlobusProfession;
  freshCounterSubtype?: 'cheese' | 'fish' | 'meat' | 'general';
  eyebrow: string;
  title: string;
  introduction: string;
  description: string;
  distinction?: string;
  tasksHeading: string;
  tasks: string[];
  requirementsHeading: string;
  requirements: string[];
  benefitsHeading: string;
  benefits: string[];
  video: string;
  poster: string;
  videoTitle: string;
  captionTracks: GlobusCaptionTrack[];
  /** Legacy single-track fields kept while stored editor projects are migrated. */
  captionsSrc?: string;
  captionsLabel?: string;
  cta: {
    label: string;
    href: string;
  };
  warning?: {
    title: string;
    text: string;
  };
}

export interface GlobusJobsOverviewContent {
  eyebrow: string;
  title: string;
  intro: string;
  videoUnavailable: string;
  videoLoading: string;
  sharedBenefitsEyebrow: string;
  sharedBenefitsTitle: string;
  sharedBenefitsText: string;
  sharedBenefits: string[];
  conditionsTitle: string;
  conditionsText: string;
}

export const requiresPorkWarning = (job: Pick<GlobusJobContent, 'profession' | 'freshCounterSubtype'>): boolean =>
  job.profession === 'butcher' || (job.profession === 'fresh-counter' && job.freshCounterSubtype === 'meat');

export const globusJobApplicationHref = (
  job: Pick<GlobusJobContent, 'profession' | 'freshCounterSubtype' | 'cta'>,
): string => {
  const isAbsolute = /^(?:https?:)?\/\//u.test(job.cta.href);
  const url = new URL(job.cta.href, 'https://app.vivaporta.org');
  url.searchParams.set('profession', job.profession);
  if (job.profession === 'fresh-counter' && job.freshCounterSubtype) {
    url.searchParams.set('specialization', job.freshCounterSubtype);
  } else {
    url.searchParams.delete('specialization');
  }
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
};

interface GlobusJobContentDependencies {
  applicationHref: (profession: GlobusProfession) => string;
  bakeryVideo: string;
  bakeryCaptionTracks?: GlobusCaptionTrack[];
  butcherVideo: string;
  butcherCaptionTracks?: GlobusCaptionTrack[];
  gastronomyVideo: string;
  gastronomyCaptionTracks?: GlobusCaptionTrack[];
  freshCounterVideo: string;
  freshCounterCaptionTracks?: GlobusCaptionTrack[];
  bakeryPoster: string;
  butcherPoster: string;
  freshCounterPoster: string;
  gastronomyPoster: string;
}

export const createGermanGlobusJobs = ({
  applicationHref,
  bakeryVideo,
  bakeryCaptionTracks,
  butcherVideo,
  butcherCaptionTracks,
  gastronomyVideo,
  gastronomyCaptionTracks,
  freshCounterVideo,
  freshCounterCaptionTracks,
  bakeryPoster,
  butcherPoster,
  freshCounterPoster,
  gastronomyPoster,
}: GlobusJobContentDependencies): GlobusJobContent[] => [
  {
    profession: 'bakery',
    eyebrow: 'Stellenausschreibung',
    title: 'Bäcker:in',
    introduction: 'Du liebst den Duft frischer Backwaren und möchtest echtes Bäckerhandwerk mit modernen Anlagen verbinden?',
    description:
      'In der GLOBUS Meisterbäckerei entstehen täglich frische Backwaren nach bewährten und regionalen Rezepturen. Du arbeitest mit echten Zutaten, modernen Produktionsanlagen und einem Team, das die Leidenschaft für gutes Bäckerhandwerk teilt.',
    tasksHeading: 'Deine Aufgaben',
    tasks: [
      'Zutaten präzise abwiegen und Teige fachgerecht ansetzen und kneten',
      'Brot, Brötchen und weitere Backwaren nach bewährten Rezepturen herstellen',
      'Backvorgänge vorbereiten, begleiten und kontrollieren',
      'Moderne Maschinen und Produktionsanlagen bedienen und pflegen',
      'Qualitäts-, Sicherheits- und Hygienevorgaben einhalten',
    ],
    requirementsHeading: 'Das solltest du mitbringen',
    requirements: [
      'Idealerweise eine Ausbildung oder Berufserfahrung im Bäckerhandwerk',
      'Freude an handwerklich hergestellten Backwaren',
      'Körperliche Belastbarkeit und eine anpackende Arbeitsweise',
      'Bereitschaft zu früher Arbeit sowie ausreichende Deutschkenntnisse für Alltag und Sicherheit',
    ],
    benefitsHeading: 'Das erwartet dich',
    benefits: ['Vollzeitstellen sind möglich', 'Langfristige und unbefristete Beschäftigungsmöglichkeiten', 'In der Regel keine Arbeit an Sonn- und Feiertagen'],
    video: bakeryVideo,
    poster: bakeryPoster,
    videoTitle: 'Video zur Stellenausschreibung als Bäcker:in bei GLOBUS',
    captionTracks: bakeryCaptionTracks?.map(track => ({ ...track })) ?? emptyGlobusCaptionTracks(),
    cta: { label: 'Jetzt als Bäcker:in bewerben', href: applicationHref('bakery') },
  },
  {
    profession: 'butcher',
    eyebrow: 'Stellenausschreibung',
    title: 'Metzger:in',
    introduction: 'Du arbeitest präzise und möchtest traditionelles Lebensmittelhandwerk mit modernen Produktionsverfahren verbinden?',
    description:
      'In der GLOBUS Fachmetzgerei stellst du Fleisch- und Wurstwaren in echter handwerklicher Produktion her. Dabei arbeitest du mit traditionellen Verfahren, bewährten Rezepturen und modernen Maschinen.',
    tasksHeading: 'Deine Aufgaben',
    tasks: [
      'Fleisch fachgerecht zerlegen, zuschneiden und für die weitere Verarbeitung vorbereiten',
      'Hauseigene Wurstwaren, Schinken und regionale Spezialitäten herstellen',
      'Kutter, Räucherofen, Wurstkessel und Füllmaschinen bedienen',
      'Auf Qualität, Sauberkeit und einen verantwortungsvollen Umgang mit Lebensmitteln achten',
      'Arbeitssicherheits- und Hygienevorgaben konsequent einhalten',
    ],
    requirementsHeading: 'Das solltest du mitbringen',
    requirements: [
      'Idealerweise eine Ausbildung oder Berufserfahrung als Metzger:in beziehungsweise Fleischer:in',
      'Freude am traditionellen Lebensmittelhandwerk',
      'Körperliche Belastbarkeit und eine anpackende Arbeitsweise',
      'Zeitliche Flexibilität sowie ausreichende Deutschkenntnisse für Alltag und Sicherheit',
    ],
    benefitsHeading: 'Das erwartet dich',
    benefits: ['Voll- und Teilzeitstellen können verfügbar sein', 'Langfristige und unbefristete Beschäftigungsmöglichkeiten', 'Fachliche Einarbeitung und Weiterbildungsangebote'],
    video: butcherVideo,
    poster: butcherPoster,
    videoTitle: 'Video zur Stellenausschreibung als Metzger:in bei GLOBUS',
    captionTracks: butcherCaptionTracks?.map(track => ({ ...track })) ?? emptyGlobusCaptionTracks(),
    cta: { label: 'Jetzt als Metzger:in bewerben', href: applicationHref('butcher') },
    warning: {
      title: 'Wichtiger Hinweis zur Tätigkeit',
      text: 'In der GLOBUS Fachmetzgerei wird auch Schweinefleisch verarbeitet. Du solltest daher bereit sein, im beruflichen Alltag mit Schweinefleisch und daraus hergestellten Produkten zu arbeiten.',
    },
  },
  {
    profession: 'fresh-counter',
    freshCounterSubtype: 'general',
    eyebrow: 'Stellenausschreibung',
    title: 'Mitarbeiter:in an der Frischetheke',
    introduction: 'Du hast Freude an hochwertigen Lebensmitteln und möchtest Kund:innen freundlich und kompetent beraten?',
    description:
      'An der Frischetheke verbindest du persönliche Beratung mit hochwertigen Lebensmitteln. Du sorgst dafür, dass die Produkte frisch, appetitlich und übersichtlich präsentiert werden und Kund:innen freundlich und kompetent beraten werden.',
    distinction:
      'Die Metzgerei steht vor allem für Herstellung und Verarbeitung. An der Frischetheke stehen Beratung, Verkauf, Präsentation und Qualitätskontrolle im Mittelpunkt. Der konkrete Thekenbereich wird vor dem Start transparent vereinbart.',
    tasksHeading: 'Deine Aufgaben',
    tasks: [
      'Kund:innen herzlich beraten und an der jeweiligen Frischetheke bedienen',
      'Produkte fachkundig erklären und verkaufen',
      'Die Theke ansprechend gestalten, auffüllen und sauber halten',
      'Regelmäßige Frische- und Qualitätskontrollen durchführen',
      'Hygienevorgaben zuverlässig einhalten',
    ],
    requirementsHeading: 'Das solltest du mitbringen',
    requirements: [
      'Freude am Kontakt mit Menschen und Interesse an hochwertigen, frischen Lebensmitteln',
      'Ein guter Blick für Ordnung, Sauberkeit und Warenpräsentation',
      'Zuverlässigkeit und Freude an der Arbeit im Team',
      'Zeitliche Flexibilität sowie ausreichende Deutschkenntnisse für Beratung und Arbeitsalltag',
    ],
    benefitsHeading: 'Das erwartet dich',
    benefits: ['Voll- und Teilzeitstellen können verfügbar sein', 'Quereinstieg kann je nach Position möglich sein', 'Strukturierte Einarbeitung und fachliche Weiterbildung'],
    video: freshCounterVideo,
    poster: freshCounterPoster,
    videoTitle: 'Video zur Stellenausschreibung als Mitarbeiter:in an der GLOBUS Frischetheke',
    captionTracks: freshCounterCaptionTracks?.map(track => ({ ...track })) ?? emptyGlobusCaptionTracks(),
    cta: { label: 'Jetzt für die Frischetheke bewerben', href: applicationHref('fresh-counter') },
    warning: {
      title: 'Wichtiger Hinweis zur Tätigkeit',
      text: 'Bei einem Einsatz im Fleischbereich wird auch Schweinefleisch verarbeitet. Du solltest bereit sein, mit Schweinefleisch zu arbeiten.',
    },
  },
  {
    profession: 'gastronomy',
    eyebrow: 'Stellenausschreibung',
    title: 'Mitarbeiter:in in der Gastronomie',
    introduction: 'Du kochst gern mit frischen Zutaten und bleibst auch dann freundlich, wenn im Restaurant viel los ist?',
    description:
      'In der GLOBUS Gastronomie bereitest du warme und kalte Gerichte frisch zu, richtest sie appetitlich an und gibst sie freundlich an die Gäste aus. Je nach Markthalle arbeitest du beispielsweise am Asia-Wok, am Hähnchengrill, am Pizzaofen oder in einem anderen gastronomischen Bereich.',
    tasksHeading: 'Deine Aufgaben',
    tasks: [
      'Warme und kalte Speisen frisch zubereiten',
      'Gerichte appetitlich anrichten und an die Gäste ausgeben',
      'Gäste freundlich beraten und bedienen',
      'Bei Bedarf das Kassensystem bedienen',
      'Arbeits-, Qualitäts- und Hygienevorgaben einhalten',
    ],
    requirementsHeading: 'Das solltest du mitbringen',
    requirements: [
      'Freude am Kochen und am Umgang mit frischen Zutaten',
      'Motivation, Zuverlässigkeit und Freundlichkeit im Kontakt mit Gästen',
      'Zeitliche Flexibilität in wechselnden Schichten von Montag bis Samstag',
      'Ausreichende Deutschkenntnisse; Quereinstieg ist bei passenden Positionen möglich',
    ],
    benefitsHeading: 'Das erwartet dich',
    benefits: ['Voll- und Teilzeitmodelle können verfügbar sein', 'Langfristige und unbefristete Beschäftigungsmöglichkeiten', 'In der Regel Feierabend spätestens zum Ladenschluss'],
    video: gastronomyVideo,
    poster: gastronomyPoster,
    videoTitle: 'Video zur Stellenausschreibung als Mitarbeiter:in in der GLOBUS Gastronomie',
    captionTracks: gastronomyCaptionTracks?.map(track => ({ ...track })) ?? emptyGlobusCaptionTracks(),
    cta: { label: 'Jetzt für die Gastronomie bewerben', href: applicationHref('gastronomy') },
  },
];

export const germanGlobusJobsOverview: GlobusJobsOverviewContent = {
  eyebrow: 'Aktuelle Stellen bei GLOBUS',
  title: 'Stellenausschreibungen bei GLOBUS',
  intro:
    'Entdecke Aufgaben, Voraussetzungen und Perspektiven in den handwerklichen und serviceorientierten Bereichen eines GLOBUS Marktes.',
  videoUnavailable: 'Das Video ist momentan nicht verfügbar. Alle Informationen zur Stelle findest du direkt daneben.',
  videoLoading: 'Video wird geladen',
  sharedBenefitsEyebrow: 'Gemeinsame Vorteile',
  sharedBenefitsTitle: 'Starke Leistungen für deinen langfristigen Start bei GLOBUS.',
  sharedBenefitsText:
    'Viele GLOBUS Stellen verbinden verlässliche Perspektiven mit zusätzlichen betrieblichen Leistungen. Welche davon für deine konkrete Position gelten, klären wir transparent mit dir.',
  sharedBenefits: [
    'Bis zu sechs Wochen Urlaub, abhängig vom Arbeitszeitmodell',
    'Mitarbeiterrabatt sowie mögliche Urlaubs- und Weihnachtsgeldleistungen',
    'Betriebliche Altersvorsorge und vermögenswirksame Leistungen je nach Stelle',
    'Weiterbildungs- und Entwicklungsmöglichkeiten',
    'Minutengenaue Zeiterfassung',
    'Je nach Tätigkeit freie Sonn- und Feiertage',
  ],
  conditionsTitle: 'Hinweis zu den Rahmenbedingungen',
  conditionsText:
    'Die konkreten Arbeitsbedingungen und Leistungen können je nach Standort und Position variieren. Arbeitszeiten, Vergütung, Einsatzort und verbindliche Leistungen werden vor einer Bewerbung transparent besprochen.',
};
